import React from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, X, ArrowRight, ShoppingBag } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import { useCart, money } from "../context/CartContext";
import { toast } from "sonner";

export const CartDrawer = () => {
  const { items, open, setOpen, setQty, removeItem, subtotal, currency, count, checkout, checkingOut } = useCart();
  const navigate = useNavigate();

  const onCheckout = async () => {
    const res = await checkout();
    if (res?.error === "failed") toast.error("Couldn't reach checkout. Try again.");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="bg-[#0a0a0a] border-l border-white/10 text-white w-full sm:max-w-md flex flex-col p-0"
      >
        <SheetHeader className="px-6 py-5 border-b border-white/10">
          <SheetTitle className="font-display font-extrabold uppercase tracking-normal text-xl text-white flex items-center gap-3">
            Your Pack
            <span className="font-sans text-xs tracking-[0.2em] text-white/40">({count})</span>
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center">
            <ShoppingBag className="w-10 h-10 text-white/20" strokeWidth={1} />
            <p className="font-sans text-sm text-white/50">Your pack is empty.</p>
            <button
              data-testid="cart-empty-shop"
              onClick={() => { setOpen(false); const el = document.getElementById("collections"); el && (window.__lenis ? window.__lenis.scrollTo(el, { offset: -80 }) : el.scrollIntoView()); navigate("/"); }}
              className="mt-2 font-sans text-xs tracking-[0.25em] text-white border-b border-white/40 pb-1 hover:border-white transition-colors"
            >
              BROWSE THE ARSENAL
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {items.map((i) => (
                <div key={i.variantId} data-testid={`cart-item-${i.variantId}`} className="flex gap-4 border-b border-white/[0.06] pb-5">
                  <div className="w-20 h-24 bg-[#141414] border border-white/10 shrink-0 overflow-hidden flex items-center justify-center">
                    {i.image && <img src={i.image} alt={i.productTitle} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-sans text-sm text-white leading-snug pr-2">{i.productTitle}</h4>
                      <button data-testid={`cart-remove-${i.variantId}`} onClick={() => removeItem(i.variantId)} className="text-white/40 hover:text-white transition-colors shrink-0">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="font-sans text-[11px] tracking-[0.1em] text-white/40 mt-1">{i.variantTitle}</p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-white/15">
                        <button data-testid={`cart-dec-${i.variantId}`} onClick={() => setQty(i.variantId, i.qty - 1)} className="px-2.5 py-1.5 text-white/60 hover:text-white"><Minus className="w-3 h-3" /></button>
                        <span className="w-8 text-center font-sans text-xs text-white">{i.qty}</span>
                        <button data-testid={`cart-inc-${i.variantId}`} onClick={() => setQty(i.variantId, i.qty + 1)} className="px-2.5 py-1.5 text-white/60 hover:text-white"><Plus className="w-3 h-3" /></button>
                      </div>
                      <span className="font-sans text-sm text-white">{money(i.price * i.qty, i.currency)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-white/10 px-6 py-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-sans text-xs tracking-[0.2em] text-white/50">SUBTOTAL</span>
                <span className="font-display font-bold text-xl text-white">{money(subtotal, currency)}</span>
              </div>
              <p className="font-sans text-[11px] text-white/35">Shipping &amp; taxes calculated at checkout.</p>
              <button
                data-testid="checkout-button"
                onClick={onCheckout}
                disabled={checkingOut}
                className="group relative w-full h-14 overflow-hidden bg-white text-black flex items-center justify-center gap-3 disabled:opacity-60"
              >
                <span className="absolute inset-0 bg-black translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
                <span className="relative font-sans text-xs tracking-[0.28em] text-black group-hover:text-white transition-colors duration-500">
                  {checkingOut ? "REDIRECTING…" : "SECURE CHECKOUT"}
                </span>
                {!checkingOut && <ArrowRight className="relative w-4 h-4 text-black group-hover:text-white transition-colors duration-500" />}
              </button>
              <p className="font-sans text-[10px] tracking-[0.15em] text-white/30 text-center">POWERED BY SHOPIFY · FULFILLED BY TAPSTITCH</p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
